import type {
  DiscountCategoryOption,
  DiscountTypeOption,
} from 'features/Procedure/state/procedure-api.type'
import {
  DISCOUNT_CATEGORY,
  findDiscountTypeById,
} from 'features/Procedure/features/treatments/components/discount-select/discount-utils'

import type { ContactSelectOption } from './patient-contact-form.schema'

export type ApplicableDiscountFields = {
  products: boolean
  procedures: boolean
}

/**
 * Which discount-type selects apply for a given discount category.
 * - Products and Procedures -> both
 * - Products Only           -> products
 * - Procedures Only         -> procedures
 * - null / unassigned       -> neither
 */
export const getApplicableDiscountFields = (
  categoryId: string | null | undefined,
): ApplicableDiscountFields => {
  switch (categoryId) {
    case DISCOUNT_CATEGORY.PRODUCTS_AND_PROCEDURES:
      return { products: true, procedures: true }
    case DISCOUNT_CATEGORY.PRODUCTS_ONLY:
      return { products: true, procedures: false }
    case DISCOUNT_CATEGORY.PROCEDURES_ONLY:
      return { products: false, procedures: true }
    default:
      return { products: false, procedures: false }
  }
}

export const toDiscountCategoryOptions = (
  categories?: ReadonlyArray<DiscountCategoryOption>,
): ContactSelectOption[] =>
  (categories ?? []).map((category) => ({ value: category.option_id, label: category.title }))

export const toDiscountTypeOptions = (
  types?: ReadonlyArray<DiscountTypeOption>,
): ContactSelectOption[] =>
  (types ?? []).map((type) => ({ value: type.option_id, label: type.title }))

export const findDiscountCategoryTitle = (
  categories: ReadonlyArray<DiscountCategoryOption> | undefined,
  id: string | null | undefined,
): string | null => {
  if (!categories || !id) return null
  return categories.find((category) => category.option_id === id)?.title ?? null
}

export const findDiscountTypeTitle = (
  types: ReadonlyArray<DiscountTypeOption> | undefined,
  id: string | null | undefined,
): string | null => findDiscountTypeById(types, id)?.title ?? null

export type DiscountTypeValues = {
  products: string | null
  procedures: string | null
}

// Pure stash/restore for the two discount-type selects when the category changes:
// a value about to be hidden is stashed; a context becoming visible again is
// restored from the stash. Returns the next field values and the next stash.
export const resolveCategoryChange = ({
  prevCategory,
  nextCategory,
  current,
  stash,
}: {
  prevCategory: string | null
  nextCategory: string | null
  current: DiscountTypeValues
  stash: DiscountTypeValues
}): { next: DiscountTypeValues; stash: DiscountTypeValues } => {
  const prev = getApplicableDiscountFields(prevCategory)
  const applicable = getApplicableDiscountFields(nextCategory)
  const next: DiscountTypeValues = { ...current }
  const nextStash: DiscountTypeValues = { ...stash }

  const resolveContext = (context: keyof DiscountTypeValues) => {
    if (prev[context] && !applicable[context]) {
      nextStash[context] = current[context]
      next[context] = null
    } else if (!prev[context] && applicable[context] && stash[context]) {
      next[context] = stash[context]
      nextStash[context] = null
    } else if (!applicable[context]) {
      next[context] = null
    }
  }

  resolveContext('products')
  resolveContext('procedures')

  return { next, stash: nextStash }
}
